import React, {FC, Fragment, useEffect, useMemo, useState} from 'react';
import {Tabs, TabPanel} from 'react-tabs';

import {
  CompetitionName,
  EventStatus,
  MatchGroup,
  MatchSimple,
  SearchTerm,
  Sport,
} from 'utils/types';

import MatchList from 'components/Common/MatchList';
import PillTabList from 'components/Common/PillTabList';
import NoData from 'components/Common/NoData';

import './styles.scss';
import Loader from 'components/Common/Loader';
import {groupMatchesByCompetition} from 'utils/matchFns';
import {format, startOfToday} from 'date-fns';
import el from 'date-fns/locale/el';
import Ad from 'components/Common/Ad';
import AdRepeated from 'components/Common/AdRepeated';
import {BECompetitionDetail} from 'API/types';

type MatchListEntry = {date: string; dayLabel: string; groups: Record<string, MatchGroup>};

interface Props {
  loading: boolean;
  search: SearchTerm;
  dateTab: number;
  matchesInDates: Record<string, MatchSimple[]> | null;
  competitions?: BECompetitionDetail[];
  isBreakpointLG: boolean;
}

// TODO: const derivetabs

const TabbedMatchLists: FC<Props> = ({
  loading,
  search,
  dateTab,
  matchesInDates,
  competitions,
  isBreakpointLG,
}: Props) => {
  const [calculating, setCalculating] = useState(false);
  const [tabs, setTabs] = useState<string[]>([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [panels, setPanels] = useState<
    {date: string; dayLabel: string; groups: Record<string, MatchGroup>}[][]
  >([]);

  // TODO: const selectedTabs = useMemo(deriveTabs(search, matchesData), [search, matchesData]);

  useEffect(() => {
    setSelectedTab(0);
  }, [dateTab]);

  useEffect(() => {
    // BIQ: does this work?
    setCalculating(true);

    if (matchesInDates) {
      // BIQ: optimize this mess
      const dateEntries = Object.entries(matchesInDates);
      let panelTabs: string[] = [];
      let panelItems: MatchListEntry[][] = [];

      if (search) {
        const today = startOfToday();
        const todayString = format(today, 'yyyy-MM-dd'); // BIQ: maybe standardize this format throughout the app, for easy changes?
        const collator = new Intl.Collator('en', {numeric: true}); // BIQ: is comparing day strings OK or maybe should convert to Date objects?

        const matchEntriesPast: MatchListEntry[] = [];
        const matchEntriesToday: MatchListEntry[] = [];
        const matchEntriesFuture: MatchListEntry[] = [];

        dateEntries.forEach(([date, matches]) => {
          if (!matches || !matches.length) return;

          const dayLabel = format(new Date(date), 'EEEE, d MMMM', {locale: el});

          const groups = groupMatchesByCompetition(matches, competitions);

          if (collator.compare(date, todayString) < 0)
            matchEntriesPast.push({date, dayLabel, groups});
          else if (collator.compare(date, todayString) > 0)
            matchEntriesFuture.push({date, dayLabel, groups});
          else matchEntriesToday.push({date, dayLabel, groups});
        });

        panelTabs = [
          ...(matchEntriesPast.length ? ['Αποτελέσματα'] : []),
          ...(matchEntriesToday.length ? ['Σήμερα'] : []),
          ...(matchEntriesFuture.length ? ['Πρόγραμμα'] : []),
        ];
        panelItems = [
          ...(matchEntriesPast.length ? [matchEntriesPast] : []),
          ...(matchEntriesToday.length ? [matchEntriesToday] : []),
          ...(matchEntriesFuture.length ? [matchEntriesFuture] : []),
        ];
      } else {
        const matchEntriesAll: MatchListEntry[] = [];
        const matchEntriesLive: MatchListEntry[] = [];

        dateEntries.forEach(([date, matches]) => {
          if (!matches || !matches.length) return;

          const dayLabel = format(new Date(date), 'EEEE, d LLLL', {locale: el});

          const allGroups = groupMatchesByCompetition(matches, competitions);
          matchEntriesAll.push({date, dayLabel, groups: allGroups});

          // live matches
          const liveMatches = matches.filter(m => m.status === EventStatus.LIVE);
          if (liveMatches.length) {
            const liveGroups = groupMatchesByCompetition(liveMatches, competitions);
            matchEntriesLive.push({date, dayLabel, groups: liveGroups});
          }
        });

        panelTabs = ['Όλοι οι Αγώνες', ...(matchEntriesLive.length ? ['Live Αγώνες'] : [])];
        panelItems = [matchEntriesAll, ...(matchEntriesLive.length ? [matchEntriesLive] : [])];
      }

      setTabs(panelTabs);
      setPanels(panelItems);
      setCalculating(false);
    }
  }, [search, matchesInDates, competitions]);

  return (
    <>
      {!calculating && matchesInDates && Object.keys(matchesInDates).length ? (
        <Tabs
          className={`TabbedMatchLists ${tabs.length < 2 ? 'TabbedMatchLists--single-tab' : ''}`}
          selectedIndex={selectedTab}
          onSelect={(index, prevIndex) => index !== prevIndex && setSelectedTab(index)}
        >
          {PillTabList({tabs}) /* component used as a function due to bug */}
          {panels.map((dates, i) => (
            <TabPanel key={tabs[i]}>
              {dates.map(({date, dayLabel, groups}, j) => (
                <Fragment key={date}>
                  <MatchList
                    label={search ? dayLabel : undefined}
                    matchGroups={groups}
                    isBreakpointLG={isBreakpointLG}
                  />
                  {
                    /* only show ads between dates if we're in search, on mobile and not at last position */
                    search && !isBreakpointLG && dates.length > 1 && j < dates.length - 1 && (
                      <AdRepeated
                        adUnitRoot="sport24.gr/Matchcenter_hp_300x250"
                        adUnitLetters={['A', 'B', 'C', 'D']}
                        scope="matchlist"
                        index={j}
                        step={1}
                        sizes={
                          j > 2
                            ? [
                                [300, 250],
                                [320, 100],
                              ]
                            : [
                                [300, 250],
                                [320, 100],
                                [320, 50],
                              ]
                        }
                      />
                    )
                  }
                </Fragment>
              ))}
            </TabPanel>
          ))}
        </Tabs>
      ) : (
        <div className="TabbedMatchLists--empty">
          {loading || calculating ? <Loader /> : <NoData />}
        </div>
      )}
    </>
  );
};

export default React.memo(TabbedMatchLists);
